import { followType } from "@/components/FollowButton/operationButton";
import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import { Violation } from "@/components/Violation";
import { PIN_PRIVATE_STATUS } from "@/constants/pin";
// import { getMuseName, isCertifyCompany } from "@/constants/areas";
import { BoardMemberInCard } from "@/components/BoardMember";
import FollowButton from "@/components/FollowButton";
import useAppStore from "@/context/store";
import { followBoard, unfollowBoard } from "@/services";
import displayNum from "@/utils/displayNum";
import getTimeDuration from "@/utils/getTimeDuration";
import { getPngBgClass } from "@/utils/hooks/usePngBg";
import imageUrl from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import useSaveListCache from "@/utils/useSaveListCache";
import classNames from "classnames";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Case, Else, If, Switch, Then, When } from "react-if";
import styles from "./index.module.less";
import Logo from "./Logo";

const BoardAction = dynamic(import("./action"), {
  ssr: false,
});

/**
 * 画板卡片
 * @param {import("../type").IBoardCardProps} props
 * @returns
 */
function BoardCard({
  className,
  record,
  hideButton = false,
  hideUser = false,
  boardType = "new",
  hideFollowCount = true,
  hideHeader = false,
  hideInfo = false,
  style,
  noClick,
  type = "default",
  disableHoverMask,
  onDelete,
  draggable,
  onFollow,
  domRef,
  isDragging,
  hideTime,
  showLikeBtn = false,
  grayFollow = false,
  canEdit = false,
  ...rest
}) {
  const user = useAppStore((state) => state.user);

  const [board, setBoard] = useState(record);
  // 获取卡片上榜信息
  useSaveListCache();
  const { listCacheMap } = useAppStore((state) => {
    return {
      listCacheMap: state.listCacheMap,
    };
  });
  const { runAsync: runFollow } = useRequest(followBoard, {
    manual: true,
  });
  const { runAsync: runUnfollow } = useRequest(unfollowBoard, {
    manual: true,
  });

  const { login } = useLogin();

  useEffect(() => {
    setBoard(record);
  }, [record]);

  /**
   * @type {IPinFile &&{ raw_text: string }[]}
   */
  const imgs = useMemo(() => {
    const list = [];
    if (board.cover?.file) {
      list.push({
        ...board.cover.file,
        is_violation: board.cover.is_private === PIN_PRIVATE_STATUS.VIOLATION,
      });
    }

    board.pins?.slice(0, 4).forEach((pin) => {
      list.push({
        ...pin.file,
        raw_text: pin?.raw_text,
        is_violation: pin.is_private === PIN_PRIVATE_STATUS.VIOLATION,
      });
    });

    return list;
  }, [board]);

  const onToggleFollow = useCallback(async () => {
    if (!user) {
      const result = await login({
        source_location: "画板关注",
      });
      if (!result) {
        return;
      }
    }

    if (board?.following) {
      const result = await runUnfollow(board.board_id);
      if (!result.err) {
        record.following = false;
        record.follow_count = board.follow_count - 1;
        const newBoard = {
          ...board,
          following: false,
          follow_count: board.follow_count - 1,
        };
        setBoard(newBoard);
        onFollow && onFollow(false, newBoard);
      }
    } else {
      const result = await runFollow(board.board_id);
      if (!result.err) {
        record.following = true;
        record.follow_count = board.follow_count + 1;
        const newBoard = {
          ...board,
          following: true,
          follow_count: board.follow_count + 1,
        };
        setBoard(newBoard);
        onFollow && onFollow(true, newBoard);
      }
    }
  }, [user, board, login, runUnfollow, record, onFollow, runFollow]);

  // 奇葩的待归类画板
  const isSpecial = useMemo(() => {
    return board?.is_private === 2;
  }, [board]);

  // 0：公开
  // 1：管理员设为私密，前端当做0处理
  // 2：待分类
  // 3：私密
  const isPrivate = useMemo(() => {
    return (
      board?.is_private && board?.is_private !== 0 && board?.is_private !== 1
    );
  }, [board]);

  // const isUserOfficial = useMemo(() => {
  //   return config?.officialUsers?.split(",").includes(board?.user?.urlname);
  // }, [config, board]);

  const updateTime = useMemo(() => {
    const createTime = record?.pins?.[0]?.created_at;
    if (createTime) {
      return getTimeDuration({
        time: createTime * 1000,
        maxShowTime: [1, "year"],
        customTimeMap: [
          ["M", "月"],
          ["d", "天"],
          ["h", "小时"],
          ["m", "分钟"],
        ],
      });
    }
    return false;
  }, [record]);

  const onImgLoad = useCallback((evt) => {
    const target = evt.target;
    target?.classList.add("transparent-img-bg");
  }, []);

  // 兼容旧版画板自定义封面的尺寸（宽高比1.5）的展示
  const isOldCustomCover = useMemo(() => {
    // 是否是自定义封面
    const isCustomCover =
      record?.cover && record?.cover?.user_id !== record.user_id;

    if (!imgs[0]) {
      return false;
    }

    const aspectRatio = imgs[0].width / imgs[0].height;

    // 判断宽高比是否为1.5 （ 1.4- 1.6 是为了允许一点误差）
    return isCustomCover && aspectRatio > 1.4 && aspectRatio < 1.6;
  }, [imgs, record?.cover, record.user_id]);

  // 列表类型
  if (type === "list") {
    return (
      <div
        className={classNames(styles.small, "board-card", {
          [styles.isDragging]: isDragging,
        })}
        {...rest}
        style={style}
        ref={domRef}
      >
        <Link
          href={`/boards/${board.board_id}`}
          className={styles.listBoardType}
        >
          <div className={styles.wrap}>
            {imgs.length ? (
              imgs[0]?.is_violation ? (
                <div className={styles.block}>
                  <Violation hideText />
                </div>
              ) : (
                <Image
                  onLoad={onImgLoad}
                  width={80}
                  height={80}
                  src={imageUrl(imgs[0], {
                    width: 80,
                    rect: "sq",
                  })}
                  showError={true}
                  alt="画板封面"
                />
              )
            ) : (
              <div className={styles.block}></div>
            )}
            <div className={styles.info}>
              <p className={styles.name} title={board.title}>
                {board.title}
              </p>
              <p>{displayNum(board.pin_count)}采集</p>
            </div>
          </div>
          {isPrivate ||
            (board.extra?.is_creation && (
              <div className={styles.tag}>
                {board.extra?.is_creation && "原创"}
                {isPrivate && <HBIcon className={styles.lock} type="ic_lock" />}
              </div>
            ))}
          {draggable && (
            <div title="拖拽排序" className={styles.dragAnchor}>
              <HBIcon type="drag_indicator" />
            </div>
          )}
        </Link>
      </div>
    );
  }

  return (
    <div
      {...rest}
      className={classNames(styles.card, className, "board-card", {
        [styles.disabledHoverMask]: disableHoverMask,
        [styles.isDragging]: isDragging,
      })}
      style={style}
      ref={domRef}
      data-dist-code={record?.custom_recommend ? "人工" : "算法"}
    >
      <div className={styles.album}>
        {!disableHoverMask && (
          <BoardAction
            canEdit={canEdit}
            board={board}
            onChangeBoard={setBoard}
            hideButton={hideButton}
            draggable={draggable}
            type={type}
            onFollow={onFollow}
            onDelete={onDelete}
            isSpecial={isSpecial}
            showLikeBtn={showLikeBtn}
          />
        )}
        <When condition={!isSpecial}>
          <Switch>
            <Case condition={board.extra?.is_creation && isPrivate}>
              <div className={styles.label}>
                <span className={styles.mr}>原创</span>
                <HBIcon type="ic_lock" />
              </div>
            </Case>
            <Case condition={board.extra?.is_creation}>
              <div className={styles.label}>原创</div>
            </Case>
            <Case condition={isPrivate}>
              <div className={styles.label}>
                <HBIcon type="ic_lock" />
              </div>
            </Case>
          </Switch>
        </When>

        {/* 推荐理由 */}
        <When condition={hideTime && board.rec_reason}>
          <div className={styles.reason}>
            <HBIcon type={"ic_hot"} />
            {board.rec_reason}
          </div>
        </When>

        {/* 图片 */}
        <a
          href={`/boards/${board.board_id}`}
          target="_blank"
          className={styles.imgWrapper}
          data-gd-expose="board_expose"
          data-gd-click="board_click"
          data-board-id={board.board_id}
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : null)}
        >
          {[styles.first, styles.second, styles.third].map((style, index) => (
            <If condition={imgs[index]} key={index}>
              <Then>
                <div
                  className={classNames([
                    styles.img,
                    style,
                    imgs[index]?.is_violation ? styles.bg : "",
                    {
                      [styles.customCoverWrap]: index === 0 && isOldCustomCover,
                    },
                  ])}
                  style={{ backgroundColor: `#${imgs[index]?.theme}` }}
                >
                  <Image
                    className={classNames(getPngBgClass(imgs[index]?.theme), {
                      [styles.customCover]: isOldCustomCover,
                    })}
                    onLoad={onImgLoad}
                    src={imageUrl(imgs[index], {
                      width: index === 0 ? 240 : 75,
                      rect: index === 0 ? "fw" : "sq",
                    })}
                    srcSet={
                      imageUrl(imgs[index], {
                        width: index === 0 ? 480 : 180,
                        rect: "sq",
                      }) + " 2x"
                    }
                    loading="lazy"
                    alt={imgs[index]?.raw_text || "采集图片"}
                    showError={true}
                  />
                </div>
              </Then>
              <Else>
                <div
                  className={classNames([styles.img, styles.bg, style])}
                  key={index}
                ></div>
              </Else>
            </If>
          ))}
        </a>
      </div>

      {/* 画板名称 */}
      {hideHeader ? null : (
        <div className={styles.header}>
          <a
            className={styles.title}
            href={`/boards/${board.board_id}`}
            target="_blank"
            title={board.title}
            {...(noClick
              ? {
                  onClick(e) {
                    e.preventDefault();
                  },
                }
              : null)}
          >
            {board.title}
          </a>

          {board?.members?.length > 0 && (
            <BoardMemberInCard
              className={styles.member}
              board={board}
              showMaxNum={3}
            />
          )}
        </div>
      )}
      {/* 关注数量 */}
      {hideInfo ? null : (
        <div className={styles.info}>
          {!!listCacheMap && !!board && !!listCacheMap[board.board_id] ? (
            <Logo listInfo={listCacheMap[board.board_id]}></Logo>
          ) : null}
          <span>{displayNum(board.pin_count)}采集</span>
          <When condition={!hideFollowCount && !isPrivate}>
            <span>{displayNum(board.follow_count)}关注</span>
          </When>
          <When condition={updateTime && !hideTime}>
            <span>{updateTime}更新</span>
          </When>
        </div>
      )}

      {/* 用户信息 */}
      {/* <When condition={!hideUser}>
        <a
          className={styles.user}
          href={`/user/${board.user?.urlname}`}
          target="_blank"
          data-gd-click="button_click"
          data-button-name="去个人页"
          data-board-id={board.board_id}
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : null)}
          title={board.user?.username}
        >
          <div className={styles.avatar}>
            <img
              alt={board.user?.username || "查看用户"}
              src={imageUrl(board.user?.avatar, { width: 75, rect: "sq" })}
              loading="lazy"
            />
          </div>
          <div className={styles.name}>{board.user?.username}</div>
          <Switch>
            <Case condition={isUserOfficial}>
              <div className={styles.official} title="花瓣官方"></div>
            </Case>
            <Case condition={board.user?.extra?.is_museuser}>
              <div
                className={classNames(styles.icon, [
                  isCertifyCompany(board?.user?.museUser?.category[0])
                    ? styles.company
                    : styles.personal,
                ])}
                title={
                  board?.user?.museUser?.category[0]
                    ? `认证${getMuseName(board.user.museUser.category[0])}`
                    : "认证设计师"
                }
              >
                <HBIcon type="register" />
              </div>
            </Case>
          </Switch>
        </a>
      </When> */}

      <When condition={!hideButton && type === "primary"}>
        <div className={styles.button}>
          <FollowButton
            followed={board?.following}
            onClick={onToggleFollow}
            showGrop={board?.user_following !== followType.unIndependent}
            data-gd-click="button_click"
            data-button-name={board?.following ? "取消关注" : "关注"}
            data-board-id={board?.board_id}
            data-concern-object-type="画板"
            data-concern-object-name={board?.title}
            boardInfo={board}
            grayFollow={grayFollow}
          />
        </div>
      </When>
    </div>
  );
}

export default React.memo(BoardCard);
